import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { setCommissionDeviceAntennaDirections, doClearApplyError } from 'redux/Commissioning';
import Button from 'components/shared/Button';
import Form from 'components/shared/Form';
import { ProgressIndicator } from 'components/shared/Progress';
import { useSyncFormStep } from '../../hooks';
import BeamSelectionControl from './BeamSelectionControl';
import CommissionErrorBox from 'components/features/Commissioning/CommissionErrorBox/CommissionErrorBox';
import styles from './BeamSelectionForm.module.css';

const defaultValues = {};

function BeamSelectionForm({ value }) {
  const dispatch = useDispatch();
  const valuesRef = useRef(defaultValues);
  const antennaDirections = useSelector((state) => state.data.commissioningGA2.antennaDirections);
  const applyError = useSelector((state) => state.data.commissioningGA2.applyError);
  
  // Add state to track if custom beam is selected and set
  const [isCustomBeamSelected, setIsCustomBeamSelected] = useState(false);
  const [isCustomBeamSet, setIsCustomBeamSet] = useState(false);
  // Add state to track if beam API is in progress
  const [isBeamApiInProgress, setIsBeamApiInProgress] = useState(false);
  
  const methods = useForm({
    mode: 'onChange',
    defaultValues: antennaDirections ?? defaultValues
  });
  const { formState, reset } = methods;
  const { isSubmitting, isDirty, isValid } = formState;

  // Listen for custom events from BeamRankListItem
  useEffect(() => {
    const handleBeamRankSelected = (event) => {
      if (event.detail && typeof event.detail.isCustom === 'boolean') {
        setIsCustomBeamSelected(event.detail.isCustom);
        if (!event.detail.isCustom) {
          // If a regular beam is selected, we don't need to wait for it to be "set"
          setIsCustomBeamSet(false);
        }
      }
    };

    const handleCustomBeamSet = (event) => {
      if (event.detail && typeof event.detail.isSet === 'boolean') {
        setIsCustomBeamSet(event.detail.isSet);
      }
    };

    const handleBeamApiStatus = (event) => {
      if (event.detail && typeof event.detail.inProgress === 'boolean') {
        setIsBeamApiInProgress(event.detail.inProgress);
      }
    };

    // Add event listeners
    window.addEventListener('beamRankSelected', handleBeamRankSelected);
    window.addEventListener('customBeamSet', handleCustomBeamSet);
    window.addEventListener('beamApiStatus', handleBeamApiStatus);

    // Clean up
    return () => {
      window.removeEventListener('beamRankSelected', handleBeamRankSelected);
      window.removeEventListener('customBeamSet', handleCustomBeamSet);
      window.removeEventListener('beamApiStatus', handleBeamApiStatus);
    };
  }, []);

  const handleSubmit = useCallback(
    (values) => {
      valuesRef.current = values;

      return dispatch(setCommissionDeviceAntennaDirections(values));
    },
    [dispatch]
  );

  const handleCloseErrorBox = useCallback(() => {
    reset();
    dispatch(doClearApplyError());
  }, [dispatch, reset]);

  useSyncFormStep(value, formState);
  useEffect(() => {
    if (formState.isSubmitSuccessful) {
      reset(valuesRef.current);
    }
  }, [formState, reset]);

  // Determine if Apply button should be disabled
  const isApplyDisabled = isSubmitting || !isValid || !isDirty || 
    (isCustomBeamSelected && !isCustomBeamSet) || isBeamApiInProgress;

  return (
    <Form methods={methods} onSubmit={handleSubmit} className={styles.root}>
      {applyError && (
        <CommissionErrorBox
          title={`Set Direction Error: ${applyError.type}`}
          message={applyError.message}
          onClose={handleCloseErrorBox}
        />
      )}
      {isSubmitting && (
        <div className={styles.progress}>
          <ProgressIndicator />
        </div>
      )}
      <div className={styles.control}>
        <BeamSelectionControl name="downlink.hpol" label="Horizontal" />
        <div className={styles.actions}>
          <Button
            type="submit"
            variant="contained"
            size="medium"
            color="primary"
            disabled={isApplyDisabled}
          >
            Apply
          </Button>
        </div>
      </div>
    </Form>
  );
}

export default BeamSelectionForm;
